import { z } from "zod";

/**
 * External-tool-grant schemas — a plugin/tool reaching a Role.
 *
 * ── WHY A GRANT AND NOT A `RoleResource` ────────────────────────────────────
 *
 * `external_tool` was a `RoleResourceType` until 2026-08-13 and could never be
 * written: `RoleResource` carries `@@unique([organizationId, resourceType,
 * resourceId])`, which is EXCLUSIVE attachment — one Role per resource per org
 * — and the product requirement is the opposite. Assem, 2026-08-13, verbatim:
 * *"role should hold a tool from shared catalogue, multiple roles will have the
 * same tool"*.
 *
 * That is the same requirement `collection` and `workspace` already carry, and
 * it is why both live in grant tables with `@@unique([roleId, <target>Id])`
 * rather than in `RoleResource`. `RoleCollectionGrant.boardId`'s own schema
 * comment states the rule: *"the same Collection is shared across Roles and
 * sits on a different board in each, which is why the placement belongs here."*
 * A tool is the third instance of it, not a new problem.
 *
 * ── WHAT THE GRANT POINTS AT ────────────────────────────────────────────────
 *
 * `externalToolId` is an `AgentExternalTool.id` — the CATALOGUE DEFINITION, not
 * the org's CONNECTION. Three independent reasons, and the first is the one
 * that decides it:
 *
 *  1. The system map keys a tool node `` `external_tool:<AgentExternalTool.id>`
 *     ``, because that is the id a workflow node carries when it calls a tool
 *     (`TOOL_BINDING_ID_KEYS`). A grant pointing at a `ToolCredentials.id`
 *     would key a card that no node can ever match, and `buildBoardLanes` puts
 *     a card matching no node in NO lane — the exact defect this whole feature
 *     exists to close.
 *  2. `AgentExternalTool.organizationId IS NULL` on the entire 2,747-row shared
 *     Pipedream/Apify catalogue, so the definition is org-agnostic and the same
 *     row is legitimately held by Roles in different tenants. Tenancy on THIS
 *     row comes from `organizationId` on the grant, never from the catalogue.
 *  3. An org can hold several connections to one tool (`ToolCredentials
 *     @@unique([organizationId, externalToolId, pipedreamAccountId])` — one per
 *     Pipedream account). A Role holds the CAPABILITY; which account executes
 *     it is resolved at run time by the tool runtime, which already does this.
 *
 * ⚠️ So a grant does NOT assert the org can run the tool. `ToolCredentials` is
 * where that lives, and the two are deliberately not coupled: a Role can be
 * given a tool before the connection exists, and a connection can be revoked
 * without silently emptying every Role that names it.
 */

/**
 * A tool's placement in a Role — the third M:N grant surface, alongside
 * `RoleCollectionGrantSchema` and `RoleWorkspaceGrantSchema`. Unlike
 * `PermissionSetGrantSchema` there is no `surface`: a tool grant is not scoped to
 * one app surface.
 *
 * ── WHY `name` AND `iconUrl` ARE NULLABLE, AND WHY THAT IS A MEASUREMENT ────
 *
 * They are not defensive. A grant genuinely OUTLIVES the catalogue row it names,
 * and the state *"the grant exists and there is nothing to name it"* is
 * REACHABLE in ordinary operation — so a non-nullable field would force this
 * layer to invent a string, which is the `Untitled tool` defect one layer down
 * rather than a fix for it.
 *
 * Two independent facts put that state in reach:
 *
 *  1. `RoleExternalToolGrant.externalToolId` is `ON DELETE CASCADE` onto
 *     `AgentExternalTool`, the 2,747-row catalogue every tenant shares, and bulk
 *     catalogue deletion is ROUTINE MAINTENANCE rather than an incident:
 *     production reads `n_tup_del = 5593` against `n_tup_ins = 59`
 *     (2026-08-13). The cascade removes the grant with the row, but a read
 *     already in flight, and any row whose display columns were cleared rather
 *     than deleted, land here nameless.
 *  2. The name read is `canOrgAccessTool`-scoped and DROPS a row carrying no
 *     usable name at all — `externalToolName` returns null for a row whose
 *     `publicName` and `privateName` are both blank after trimming. A row can
 *     therefore be reachable and unnameable at once, which is exactly the
 *     situation a placeholder would paper over.
 *
 * 🔴 STAGING CANNOT DEMONSTRATE ANY OF THIS AND MUST NOT BE READ AS EVIDENCE
 * AGAINST IT. Its `AgentExternalTool` `n_tup_del` is **2**, so the catalogue
 * there is effectively append-only and every grant resolves a name. A staging
 * run that never produces a null is a statement about staging's deletion
 * history, never about whether the null arm is reachable.
 *
 * `null` means *no display read could name this row*. It never means *the field
 * was not asked for*: every response carrying this schema fills both fields from
 * the same read, so a consumer may render the placeholder on null without
 * checking which door served it.
 */
export const RoleExternalToolGrantSchema = z.object({
  id: z.string(),
  roleId: z.string(),
  organizationId: z.string(),
  externalToolId: z.string(),
  /** The catalogue row's display name, or null — see the schema docblock. */
  name: z.string().nullable(),
  /** The catalogue row's brand mark, or null. Absent on a named row is normal. */
  iconUrl: z.string().nullable(),
  createdAt: z.string()
});

export type RoleExternalToolGrant = z.infer<typeof RoleExternalToolGrantSchema>;

/**
 * ── WHY THE READ'S OUTCOME IS ON THE WRAPPER AND NOT ON THE ROW ─────────────
 *
 * A row's `name: null` answers *"could this row be named?"* and carries two
 * causes that the client must treat as OPPOSITES: the catalogue was read and
 * this tool is genuinely gone, or the catalogue read FAILED and nothing is known
 * about any tool on the list. The row cannot separate them, because in the
 * second case every row is null for one reason that is not about any row.
 *
 * So the outcome belongs where the failure actually happens — the read is ONE
 * batch per request, not one per grant. Repeating it on each row would be the
 * same value copied N times, which is a denormalisation waiting to diverge.
 *
 * 🔴 THE CONSUMER IS THE REASON THIS EXISTS, AND IT IS NOT HYPOTHETICAL.
 * `ResolvedNameCell` (frontend, NEX-3611) takes a REQUIRED `directoryUnavailable`
 * prop because a null-per-row directory failure once made a whole tab assert
 * `Introuvable (espace de travail)` — *"this does not exist"* — about every row,
 * from one dropped request. Without this field the client must either assert
 * deletion on every failed read, or never assert it at all; and production
 * measurement says deletion is the COMMON case (`AgentExternalTool`
 * `n_tup_del = 5593` against `n_tup_ins = 59`), so withholding always is a real
 * loss rather than a safe default.
 *
 * The field is named for the FAILURE rather than the success so that the client
 * needs no negation to consume it — it is read straight into
 * `directoryUnavailable`. A name whose docblock has to explain that it does not
 * mean what it says is the wrong name.
 */
export const RoleExternalToolGrantsListResponseSchema = z.object({
  grants: z.array(RoleExternalToolGrantSchema),
  /**
   * Whether the catalogue display read FAILED for this response.
   *
   * `true` means no tool on this list could be looked up, so every `name` in
   * `grants` is null for a reason that is not about any individual tool. A
   * client must withhold its claim across the whole list rather than assert
   * deletion N times.
   *
   * `false` means the read answered, so a null `name` beside it is the
   * catalogue's own answer — that tool is genuinely gone, out of this
   * organisation's reach, or carries no usable name after trimming. That is
   * exactly when a client MAY say so.
   *
   * An EMPTY `grants` array reports `false`: no read was needed, so nothing
   * failed, and there is no row to withhold a claim about. `true` there would
   * render an empty tab as a broken directory.
   */
  displayReadFailed: z.boolean()
});

export const RoleExternalToolGrantCreateBodySchema = z.object({
  externalToolId: z.string().min(1)
});

export const RoleExternalToolGrantPathVarsSchema = z.object({
  id: z.string().uuid(),
  grantId: z.string().uuid()
});

/** Deleting a grant is idempotent — `removed` says whether a row actually went. */
export const RoleExternalToolGrantDeleteResponseSchema = z.object({
  removed: z.boolean()
});
